<template>
  <div ref="searchRoot" class="search">
    <div
      class="search-input-wrap"
      :class="{ 'search-input-wrap--active': showResults }"
    >
      <svg
        class="search-icon"
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="10.8"
          cy="10.8"
          r="6.8"
          stroke="currentColor"
          stroke-width="1.8"
        />
        <path
          d="m16 16 5 5"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />
      </svg>
      <input
        v-model="query"
        type="search"
        placeholder="Buscar…"
        aria-label="Buscar"
        @focus="showResults = query.trim().length > 0"
        @keydown.enter="handleSubmit"
      />
      <n-spin v-if="loading" size="small" />
    </div>

    <div v-if="showResults" class="search-results">
      <div v-if="loading" class="search-status">Buscando…</div>

      <template v-else>
        <section v-if="users.length" class="result-section">
          <h3>Usuarios</h3>
          <button
            v-for="user in users"
            :key="user.id"
            type="button"
            class="user-result"
            @click="goToProfile(user.nickname)"
          >
            <UserAvatar :name="user.nickname" :size="34" />
            <span>@{{ user.nickname }}</span>
          </button>
        </section>

        <div v-if="!users.length" class="search-status">
          No se han encontrado usuarios. Pulsa Enter para buscar frases.
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "lodash";
import UserService from "@/services/user.service.js";
import UserAvatar from "./UserAvatar.vue";

const router = useRouter();
const searchRoot = ref(null);
const query = ref("");
const users = ref([]);
const loading = ref(false);
const showResults = ref(false);

const search = debounce(async (value) => {
  const term = value.trim();

  if (!term) {
    users.value = [];
    loading.value = false;
    showResults.value = false;
    return;
  }

  loading.value = true;
  showResults.value = true;

  try {
    const data = await UserService.searchUsers(term);
    users.value = Array.isArray(data) ? data : data.users || [];
  } catch (error) {
    users.value = [];
    console.error("No se pudo realizar la búsqueda:", error);
  } finally {
    loading.value = false;
  }
}, 350);

watch(query, (value) => search(value));

function goToProfile(nickname) {
  showResults.value = false;
  query.value = "";
  router.push({ name: "profile", params: { nickname } });
}

// Enter: manda a la vista de resultados (usuarios + feed de frases
// filtradas), en vez de mostrar las frases en el propio desplegable.
function handleSubmit() {
  const term = query.value.trim();
  if (!term) return;

  showResults.value = false;
  router.push({ name: "search", query: { q: term } });
}

function closeResults() {
  showResults.value = false;
}

function handleDocumentClick(event) {
  if (!searchRoot.value?.contains(event.target)) {
    closeResults();
  }
}

document.addEventListener("click", handleDocumentClick);

onBeforeUnmount(() => {
  search.cancel();
  document.removeEventListener("click", handleDocumentClick);
});
</script>

<style scoped>
.search {
  position: relative;
  flex: 1;
  max-width: 520px;
  margin: 0 auto;
}

/*
 * A partir de aquí (mismo umbral que SideNav.vue, donde ya hay hueco de
 * sobra) se centra de forma absoluta respecto al ancho total del header,
 * en vez de vía flexbox: el logo y el menú de usuario no miden lo mismo,
 * así que centrar en el hueco que dejan entre ambos (flex + margin:auto)
 * no coincide con el centro real de la pantalla.
 */
@media (min-width: 1150px) {
  .search {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    margin: 0;
  }
}

.search-input-wrap {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 42px;
  padding: 0 13px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff8ec;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
}

.search-input-wrap:focus-within,
.search-input-wrap--active {
  border-color: var(--melon);
  background: #fffaf1;
  box-shadow: 0 0 0 3px rgba(255, 122, 89, 0.1);
}

.search-icon {
  flex-shrink: 0;
  color: var(--ink-soft);
}

.search-input-wrap input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 14px;
}

.search-input-wrap input::placeholder {
  color: var(--ink-soft);
}

.search-input-wrap input::-webkit-search-cancel-button {
  cursor: pointer;
}

.search-results {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  right: 0;
  z-index: 1;
  max-height: min(620px, calc(100vh - 100px));
  overflow-y: auto;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: #fffdf8;
  box-shadow: 0 12px 32px rgba(43, 32, 24, 0.12);
}

.result-section + .result-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--line);
}

.result-section h3 {
  margin: 4px 8px 7px;
  font-family: "Fraunces", serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.user-result {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.user-result:hover {
  background: var(--paper-deep);
}

.user-result span:last-child {
  font-size: 14px;
  font-weight: 600;
}

.search-status {
  padding: 24px 12px;
  text-align: center;
  color: var(--ink-soft);
  font-size: 14px;
}

@media (max-width: 700px) {
  .search {
    max-width: none;
  }
}
</style>